import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import PageShell from "@/components/PageShell";
import { Lightbox } from "@/components/Lightbox";
import { api, API } from "@/lib/api";
import { trackClick } from "@/lib/useSponsors";
import {
  Phone, MessageCircle, Globe, MapPin, Clock, Instagram, Facebook,
  Tag, ArrowLeft, Sparkles, Loader2, Share2, Check,
} from "lucide-react";

const digits = (s) => (s || "").replace(/[^\d]/g, "");

export default function SponsorProfilePage() {
  const { slug } = useParams();
  const [s, setS] = useState(undefined); // undefined=loading, null=notfound
  const [lightbox, setLightbox] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    setS(undefined);
    api.get(`/sponsors/slug/${slug}`)
      .then(({ data }) => { if (alive) setS(data); })
      .catch(() => { if (alive) setS(null); });
    return () => { alive = false; };
  }, [slug]);

  if (s === undefined) {
    return <PageShell><div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-navy/40" /></div></PageShell>;
  }
  if (s === null) {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl font-black text-navy" style={{ fontFamily: "Outfit" }}>Patrocinador no encontrado</h1>
          <Link to="/patrocinadores" className="mt-6 inline-flex items-center gap-2 text-navy font-semibold hover:text-gold"><ArrowLeft className="h-4 w-4" /> Volver a patrocinadores</Link>
        </div>
      </PageShell>
    );
  }

  const wa = digits(s.whatsapp);
  const tel = digits(s.phone);
  const mapsSrc = s.maps_url
    ? s.maps_url
    : s.address ? `https://www.google.com/maps?q=${encodeURIComponent(s.address)}&output=embed` : null;

  const galleryItems = (Array.isArray(s.gallery) ? s.gallery : []).map((url, i) => ({ url, alt: `${s.name} ${i + 1}` }));
  const navGallery = (dir) => setLightbox((i) => (i === null ? i : (i + dir + galleryItems.length) % galleryItems.length));

  const shareUrl = `${API}/og/patrocinadores/${slug}`;
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: s.name, text: s.name, url: shareUrl }); return; } catch { /* cancel */ }
    }
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { window.prompt("Copiá el enlace:", shareUrl); }
  };

  const CTA = ({ href, icon: Icon, label, primary, onClick, external = true }) => (
    <a href={href} onClick={onClick} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
       data-testid={`sponsor-cta-${label.toLowerCase()}`}
       className={`inline-flex items-center gap-2 rounded-xl px-6 py-3.5 font-bold transition-transform hover:-translate-y-0.5 ${primary ? "bg-gold text-navy" : "bg-white/10 border border-white/25 text-white hover:bg-white/20"}`}>
      <Icon className="h-5 w-5" /> {label}
    </a>
  );

  return (
    <PageShell crumbs={[{ label: "Sponsors", to: "/patrocinadores" }, { label: s.name }]}>
      {/* Hero */}
      <section className="relative bg-[#0B1F5B] text-white overflow-hidden" data-testid="sponsor-profile">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(212,160,23,0.16), transparent 55%)" }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
          <Link to="/patrocinadores" className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-sm font-semibold mb-8"><ArrowLeft className="h-4 w-4" /> Patrocinadores</Link>
          <div className="relative mx-auto flex items-center justify-center h-32">
            <span aria-hidden className="absolute h-40 w-56 rounded-full bg-white/[0.06] blur-2xl" />
            {s.logo_url
              ? <img src={s.logo_url} alt={`Logo de ${s.name}`} className="relative max-h-32 w-auto object-contain drop-shadow-[0_8px_24px_rgba(0,0,0,0.4)]" />
              : <span className="relative text-4xl font-black" style={{ fontFamily: "Outfit" }}>{s.name}</span>}
          </div>
          <h1 className="mt-6 text-3xl sm:text-4xl font-black tracking-tight text-white" style={{ fontFamily: "Outfit" }}>{s.name}</h1>
          {s.category && <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-gold"><Tag className="h-4 w-4" /> {s.category}</div>}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {tel && <CTA href={`tel:${s.phone}`} icon={Phone} label="Llamar" external={false} onClick={() => trackClick(s.id)} />}
            {wa && <CTA href={`https://wa.me/${wa}`} icon={MessageCircle} label="WhatsApp" primary onClick={() => trackClick(s.id)} />}
            {s.url && <CTA href={s.url} icon={Globe} label="Visitar" onClick={() => trackClick(s.id)} />}
            <button type="button" onClick={share} data-testid="sponsor-share"
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3.5 font-bold transition-transform hover:-translate-y-0.5 bg-white/10 border border-white/25 text-white hover:bg-white/20">
              {copied ? <><Check className="h-5 w-5" /> Copiado</> : <><Share2 className="h-5 w-5" /> Compartir</>}
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-3 gap-8">
        {/* Columna principal */}
        <div className="lg:col-span-2 space-y-8">
          {s.description && (
            <div>
              <h2 className="text-lg font-black text-navy" style={{ fontFamily: "Outfit" }}>Sobre la empresa</h2>
              <p className="mt-3 text-gray-600 leading-relaxed whitespace-pre-line">{s.description}</p>
            </div>
          )}

          {s.promotions && (
            <div className="rounded-2xl bg-gold/10 border border-gold/30 p-5">
              <div className="flex items-center gap-2 text-navy font-black" style={{ fontFamily: "Outfit" }}><Sparkles className="h-5 w-5 text-gold" /> Promociones</div>
              <p className="mt-2 text-gray-700 whitespace-pre-line">{s.promotions}</p>
            </div>
          )}

          {Array.isArray(s.gallery) && s.gallery.length > 0 && (
            <div>
              <h2 className="text-lg font-black text-navy" style={{ fontFamily: "Outfit" }}>Galería</h2>
              <div className="mt-4 flex gap-4 overflow-x-auto no-scrollbar snap-x pb-2" data-testid="sponsor-gallery">
                {s.gallery.map((img, i) => (
                  <button key={i} type="button" onClick={() => setLightbox(i)} data-testid={`sponsor-gallery-${i}`}
                    className="shrink-0 snap-center cursor-zoom-in group">
                    <img src={img} alt={`${s.name} ${i + 1}`} loading="lazy" className="h-48 w-72 object-cover rounded-xl border border-gray-100 transition-transform duration-300 group-hover:scale-[1.02]" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {mapsSrc && (
            <div>
              <h2 className="text-lg font-black text-navy" style={{ fontFamily: "Outfit" }}>Ubicación</h2>
              <iframe title="Mapa" src={mapsSrc} loading="lazy" className="mt-4 w-full h-72 rounded-2xl border border-gray-100" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          )}
        </div>

        {/* Columna de contacto */}
        <aside className="space-y-4">
          <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-black text-navy" style={{ fontFamily: "Outfit" }}>Contacto</h2>
            <ul className="mt-4 space-y-3 text-sm">
              {s.address && <li className="flex items-start gap-3 text-gray-700"><MapPin className="h-5 w-5 text-gold shrink-0" /> {s.address}</li>}
              {s.phone && <li className="flex items-center gap-3 text-gray-700"><Phone className="h-5 w-5 text-gold shrink-0" /> <a href={`tel:${s.phone}`} className="hover:text-navy">{s.phone}</a></li>}
              {s.whatsapp && <li className="flex items-center gap-3 text-gray-700"><MessageCircle className="h-5 w-5 text-gold shrink-0" /> <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="hover:text-navy">{s.whatsapp}</a></li>}
              {s.hours && <li className="flex items-start gap-3 text-gray-700"><Clock className="h-5 w-5 text-gold shrink-0" /> <span className="whitespace-pre-line">{s.hours}</span></li>}
            </ul>
            {s.url && (
              <a href={s.url} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackClick(s.id)} data-testid="sponsor-official-site"
                 className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-bold px-5 py-3 rounded-xl hover:bg-[#16235a] transition-colors">
                <Globe className="h-4 w-4" /> Visitar sitio oficial
              </a>
            )}
            {(s.instagram || s.facebook || s.url) && (
              <div className="mt-5 flex items-center gap-3">
                {s.instagram && <a href={s.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="h-10 w-10 rounded-full bg-navy/5 flex items-center justify-center text-navy hover:bg-gold hover:text-white transition-colors"><Instagram className="h-5 w-5" /></a>}
                {s.facebook && <a href={s.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="h-10 w-10 rounded-full bg-navy/5 flex items-center justify-center text-navy hover:bg-gold hover:text-white transition-colors"><Facebook className="h-5 w-5" /></a>}
                {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" aria-label="Sitio web" className="h-10 w-10 rounded-full bg-navy/5 flex items-center justify-center text-navy hover:bg-gold hover:text-white transition-colors"><Globe className="h-5 w-5" /></a>}
              </div>
            )}
          </div>
        </aside>
      </div>

      <AnimatePresence>
        {lightbox !== null && galleryItems.length > 0 && (
          <Lightbox items={galleryItems} index={lightbox} onClose={() => setLightbox(null)} onNav={navGallery} />
        )}
      </AnimatePresence>
    </PageShell>
  );
}
